import { isTruthy } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import {
  Icon,
  IconName,
  IconSize,
  ICON_SELECTOR,
  OpenInNewIcon,
} from '@ardoq/icons';
import styled, { css } from 'styled-components';
import {
  ComponentProps,
  ComponentWithDropdown,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { forwardRef } from 'react';
import { BADGE_COLOR_MAP, BADGE_ICON_MAP, BADGE_LABEL_MAP } from './consts';
import { BadgeLabel, commonStyling } from './atoms';
import { StatusType } from '../types';
import {
  composeCssStatesColorBlock,
  isInteractiveBadge,
  isInteractiveBadgeProps,
  isScenarioOrDefaultBadge,
} from './utils';
import {
  BadgeDropdownItem,
  BadgeProperties,
  BetaBadgeOptionKey,
  BetaBadgeProperties,
  InteractiveBadgeProps,
  InteractiveBetaBadgeProperties,
  NewBadgeProperties,
  ReadonlyBadgeProps,
} from './types';
import {
  badgeActive,
  badgeActiveIconColor,
  badgeActiveTextColor,
  badgeHover,
  badgeHoverIconColor,
  badgeHoverTextColor,
} from './brandColors';
import { FlexBox } from '@ardoq/layout';
import { t } from '@ardoq/i18n';

const BadgeContainer = styled.div<{
  $badgeType: StatusType;
}>`
  ${commonStyling};
  ${({ $badgeType = StatusType.DEFAULT }) =>
    composeCssStatesColorBlock(BADGE_COLOR_MAP[$badgeType])};
`;

export const BADGE_SELECTOR = BadgeContainer.toString;

const InteractiveBadgeContainer = styled.button<{
  $badgeType: StatusType;
  $isActive: boolean;
}>`
  ${commonStyling};
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${({ $badgeType }) => badgeHover[$badgeType]};
    border-color: ${({ $badgeType }) => badgeHover[$badgeType]};
    color: ${({ $badgeType }) => badgeHoverTextColor[$badgeType]};
    ${ICON_SELECTOR} {
      color: ${({ $badgeType }) => badgeHoverIconColor[$badgeType]};
    }
  }

  ${({ $badgeType = StatusType.DEFAULT }) =>
    composeCssStatesColorBlock(BADGE_COLOR_MAP[$badgeType])};
  ${({ $isActive, $badgeType }) =>
    $isActive &&
    css`
      background: ${badgeActive[$badgeType]};
      border-color: ${badgeActive[$badgeType]};
      color: ${badgeActiveTextColor[$badgeType]};
      ${ICON_SELECTOR} {
        color: ${badgeActiveIconColor[$badgeType]};
      }
    `}
`;

export const ReadonlyBadge = ({
  label,
  statusType,
  iconName,
  'data-tooltip-text': tooltipText,
}: ReadonlyBadgeProps) => (
  <BadgeContainer
    style={{ cursor: 'default', whiteSpace: 'nowrap' }}
    $badgeType={statusType}
    data-tooltip-text={tooltipText}
    aria-label={label}
  >
    {iconName && <Icon iconName={iconName} iconSize={IconSize.SMALL} />}
    <BadgeLabel label={label} />
  </BadgeContainer>
);

const InteractiveBadgeButton = forwardRef<
  HTMLButtonElement,
  { onMenuOpen: () => void; isMenuOpened: boolean }
>(({ onMenuOpen, isMenuOpened }, ref) => (
  <InteractiveBadgeContainer
    $badgeType={StatusType.BETA}
    $isActive={isMenuOpened}
    onClick={onMenuOpen}
    ref={ref}
  >
    <Icon
      iconSize={IconSize.SMALL}
      iconName={BADGE_ICON_MAP[StatusType.BETA]}
    />
    <BadgeLabel label="Beta" />
    <FlexBox justify="center" align="center" gap="small">
      <Icon
        iconSize={IconSize.SMALL}
        iconName={isMenuOpened ? IconName.ARROW_UP : IconName.ARROW_DOWN}
      />
    </FlexBox>
  </InteractiveBadgeContainer>
));

const DEFAULT_OPTIONS_ORDER: BetaBadgeOptionKey[] = [
  BetaBadgeOptionKey.KNOWLEDGE_BASE,
  BetaBadgeOptionKey.SHARE_FEEDBACK,
  BetaBadgeOptionKey.CHANGELOG,
  BetaBadgeOptionKey.VIEW_INTRODUCTION,
  BetaBadgeOptionKey.TAKE_THE_TOUR,
];

const getLabel = (
  key: BetaBadgeOptionKey,
  i18nKey: string,
  defaultValue: string,
  i18nLocale: InteractiveBetaBadgeProperties['i18nLocale'],
  labelOverrides: InteractiveBetaBadgeProperties['labelOverrides']
): string =>
  t(
    i18nLocale ?? {},
    labelOverrides?.[key]?.i18nKey ?? i18nKey,
    labelOverrides?.[key]?.defaultValue ?? defaultValue
  );

const InteractiveBetaBadge = ({
  knowledgeBaseUrl,
  shareFeedbackUrl,
  changelogUrl,
  openOnboardingModal,
  onTakeTour,
  trackEvent,
  i18nLocale,
  optionsOrder = DEFAULT_OPTIONS_ORDER,
  labelOverrides,
}: InteractiveBetaBadgeProperties) => {
  const optionsByKey: Record<BetaBadgeOptionKey, DropdownOption | null> = {
    knowledgeBase: knowledgeBaseUrl
      ? {
          type: DropdownOptionType.OPTION,
          label: getLabel(
            BetaBadgeOptionKey.KNOWLEDGE_BASE,
            'statusUi:betaBadge.knowledgeBase',
            'Why am I seeing this?',
            i18nLocale,
            labelOverrides
          ),
          iconName: IconName.KNOWLEDGE_BASE,
          onClick: () => {
            trackEvent?.('Interactive beta badge (knowledge base)');
            window.open(knowledgeBaseUrl, '_blank');
          },
        }
      : null,
    shareFeedback: shareFeedbackUrl
      ? {
          type: DropdownOptionType.OPTION,
          label: getLabel(
            BetaBadgeOptionKey.SHARE_FEEDBACK,
            'statusUi:betaBadge.shareFeedback',
            'Share feedback',
            i18nLocale,
            labelOverrides
          ),
          iconName: IconName.PRESENTATION,
          rightContent: <OpenInNewIcon color={colors.icon} />,
          onClick: () => {
            trackEvent?.('Interactive beta badge (share feedback)');
            window.open(shareFeedbackUrl, '_blank');
          },
        }
      : null,
    changelog: changelogUrl
      ? {
          type: DropdownOptionType.OPTION,
          label: getLabel(
            BetaBadgeOptionKey.CHANGELOG,
            'statusUi:betaBadge.viewChangelog',
            'View changelog',
            i18nLocale,
            labelOverrides
          ),
          iconName: IconName.UPDATE,
          rightContent: <OpenInNewIcon color={colors.icon} />,
          onClick: () => {
            trackEvent?.('Interactive beta badge (changelog)');
            window.open(changelogUrl, '_blank');
          },
        }
      : null,
    viewIntroduction: openOnboardingModal
      ? {
          type: DropdownOptionType.OPTION,
          label: getLabel(
            BetaBadgeOptionKey.VIEW_INTRODUCTION,
            'statusUi:betaBadge.viewIntroduction',
            'View introduction',
            i18nLocale,
            labelOverrides
          ),
          iconName: IconName.INFO,
          onClick: () => {
            trackEvent?.('Interactive beta badge (view introduction)');
            openOnboardingModal();
          },
        }
      : null,
    takeTour: onTakeTour
      ? {
          type: DropdownOptionType.OPTION,
          label: getLabel(
            BetaBadgeOptionKey.TAKE_THE_TOUR,
            'statusUi:betaBadge.takeTour',
            'Take the tour',
            i18nLocale,
            labelOverrides
          ),
          iconName: IconName.VIDEO_TUTORIALS,
          onClick: () => {
            trackEvent?.('Interactive beta badge (take the tour)');
            onTakeTour();
          },
        }
      : null,
  };

  const options = optionsOrder.map(key => optionsByKey[key]).filter(isTruthy);

  return (
    <ComponentWithDropdown
      Component={InteractiveBadgeButton}
      options={options}
    />
  );
};

const GenericInteractiveBadgeButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<HTMLButtonElement> & {
    statusType: InteractiveBadgeProps['statusType'];
    label: string;
    iconName?: IconName;
  }
>(({ onMenuOpen, isMenuOpened, statusType, label, iconName }, ref) => (
  <InteractiveBadgeContainer
    $badgeType={statusType}
    $isActive={isMenuOpened}
    onClick={onMenuOpen}
    ref={ref}
  >
    {iconName && <Icon iconSize={IconSize.SMALL} iconName={iconName} />}
    <BadgeLabel label={label} />
    <FlexBox justify="center" align="center" gap="small">
      <Icon
        iconSize={IconSize.SMALL}
        iconName={isMenuOpened ? IconName.ARROW_UP : IconName.ARROW_DOWN}
      />
    </FlexBox>
  </InteractiveBadgeContainer>
));

const toBadgeDropdownOption = (item: BadgeDropdownItem): DropdownOption => {
  if (item.type === 'link') {
    const target = item.target ?? '_blank';
    return {
      type: DropdownOptionType.OPTION,
      label: item.label,
      leftIcon: item.leftIcon ? { name: item.leftIcon.name } : undefined,
      rightContent:
        target === '_blank' ? (
          <OpenInNewIcon color={colors.icon} size={IconSize.EXTRA_SMALL} />
        ) : undefined,
      onClick: () => window.open(item.href, target),
    };
  }
  return {
    type: DropdownOptionType.OPTION,
    label: item.label,
    leftIcon: item.leftIcon ? { name: item.leftIcon.name } : undefined,
    onClick: item.onClick,
  };
};

export const InteractiveBadge = ({
  statusType,
  options,
}: InteractiveBadgeProps) => (
  <ComponentWithDropdown
    Component={GenericInteractiveBadgeButton}
    componentProps={{
      statusType,
      label: BADGE_LABEL_MAP[statusType],
      iconName: BADGE_ICON_MAP[statusType],
    }}
    options={options.map(toBadgeDropdownOption)}
  />
);

export const BetaBadge = (props: BetaBadgeProperties) => {
  return isInteractiveBadge(props) ? (
    <InteractiveBetaBadge {...props} />
  ) : (
    <ReadonlyBadge
      label="Beta"
      statusType={StatusType.BETA}
      iconName={BADGE_ICON_MAP[StatusType.BETA]}
    />
  );
};

export const NewBadge = (props: NewBadgeProperties) => {
  const label =
    props.label ?? t(props.i18nLocale ?? {}, 'statusUi:newBadge.label', 'New');
  return (
    <ReadonlyBadge
      label={label}
      statusType={StatusType.NEW}
      iconName={BADGE_ICON_MAP[StatusType.NEW]}
    />
  );
};

export const TrialBadge = () => {
  return (
    <ReadonlyBadge
      label="Trial"
      statusType={StatusType.TRIAL}
      iconName={BADGE_ICON_MAP[StatusType.TRIAL]}
    />
  );
};

export const Badge = (props: BadgeProperties) => {
  if (isInteractiveBadgeProps(props)) {
    return <InteractiveBadge {...props} />;
  }
  return (
    <ReadonlyBadge
      label={props.label}
      statusType={props.statusType}
      iconName={
        isScenarioOrDefaultBadge(props)
          ? props.iconName
          : BADGE_ICON_MAP[props.statusType]
      }
      data-tooltip-text={props['data-tooltip-text']}
    />
  );
};

export const CustomBadge = (props: ReadonlyBadgeProps) => {
  return (
    <ReadonlyBadge
      label={props.label}
      statusType={props.statusType}
      iconName={props.iconName}
    />
  );
};
